// src/components/common/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, MessageCircle } from 'lucide-react';
import SideCart from './SideCart';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navbarSettings, setNavbarSettings] = useState({});
  const [settings, setSettings] = useState({
    logo: '',
    store_name: 'MAISON.LUXE',
  });
  const [logoError, setLogoError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);

  const loadCategories = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();

      if (data.success && data.settings) {
        let categoriesArray = [];
        let labelsObj = {};

        if (data.settings.categories) {
          try {
            const parsed = JSON.parse(data.settings.categories);
            categoriesArray = parsed;
          } catch (e) {
            console.error('Error parsing categories:', e);
          }
        }

        if (data.settings.category_labels) {
          try {
            const parsedLabels = JSON.parse(data.settings.category_labels);
            labelsObj = parsedLabels;
          } catch (e) {
            console.error('Error parsing category labels:', e);
          }
        }

        // Only update state if we actually got real categories — never
        // silently replace whatever's currently showing with hardcoded
        // placeholder data just because this one request had an issue.
        if (categoriesArray.length > 0) {
          setCategories(categoriesArray.slice(0, 4));
          setCategoryLabels(labelsObj);
        }
      }
    } catch (error) {
      // Deliberately NOT overwriting categories/categoryLabels here anymore.
      // A failed request should leave the existing (or empty) state alone,
      // not silently swap in old Electronics/Clothing/Books/Accessories
      // placeholder data that has nothing to do with this store.
      console.error('Error loading categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadNavbarSettings = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setNavbarSettings(data.settings);
        setSettings(prev => ({ ...prev, ...data.settings }));
        applyNavbarStyles(data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const applyNavbarStyles = (settings) => {
    const navbar = document.querySelector('.navbar');
    const navLinks = document.querySelectorAll('.nav-link');
    const navContainer = document.querySelector('.nav-container');

    if (!navbar) return;

    if (settings.navbar_bg_color) {
      navbar.style.setProperty('background', settings.navbar_bg_color, 'important');
      navbar.style.setProperty('backgroundColor', settings.navbar_bg_color, 'important');
    }

    if (settings.navbar_height) {
      navbar.style.setProperty('--navbar-row-height', `${settings.navbar_height}px`);
    }

    if (settings.navbar_border_bottom) {
      navbar.style.borderBottom = `${settings.navbar_border_width || 1}px solid ${settings.navbar_border_color || '#e0e0e0'}`;
    } else {
      navbar.style.borderBottom = 'none';
    }

    if (settings.navbar_shadow) {
      navbar.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
    } else {
      navbar.style.boxShadow = 'none';
    }

    navLinks.forEach(link => {
      if (settings.navbar_font_size) {
        link.style.fontSize = `${settings.navbar_font_size}px`;
      }
      if (settings.navbar_font_weight) {
        link.style.fontWeight = settings.navbar_font_weight;
      }
      if (settings.navbar_text_transform) {
        link.style.textTransform = settings.navbar_text_transform;
      }
      if (settings.navbar_letter_spacing) {
        link.style.letterSpacing = `${settings.navbar_letter_spacing}px`;
      }
      if (settings.navbar_text_color) {
        link.style.color = settings.navbar_text_color;
      }
    });

    if (settings.navbar_hover_color) {
      let styleTag = document.getElementById('navbar-hover-style');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'navbar-hover-style';
        document.head.appendChild(styleTag);
      }
      styleTag.textContent = `.nav-link:hover { color: ${settings.navbar_hover_color} !important; }`;
    }

    if (settings.navbar_active_color) {
      let activeStyleTag = document.getElementById('navbar-active-style');
      if (!activeStyleTag) {
        activeStyleTag = document.createElement('style');
        activeStyleTag.id = 'navbar-active-style';
        document.head.appendChild(activeStyleTag);
      }
      activeStyleTag.textContent = `.nav-link.active { color: ${settings.navbar_active_color} !important; }`;
    }

    if (navContainer) {
      navContainer.style.maxWidth = '1280px';
    }
  };

  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart')) || [];
      setCartCount(cart.reduce((total, item) => total + (item.quantity || 1), 0));
    };
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    return () => window.removeEventListener('cartUpdated', updateCartCount);
  }, []);

  const getCategoryDisplayName = (categoryKey) => {
    return categoryLabels[categoryKey] || categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
  };

  const handleCategoryClick = (category, e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    // Force a clean URL navigation. 
    navigate(`/shop?category=${category}`, { replace: true });
  };

  const handleShopClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    navigate('/shop');
  };

  const handleSaleClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    navigate('/shop?sort=sale');
  };

  const handleNewArrivalsClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    navigate('/shop?sort=new-arrivals');
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadCategories();
    loadNavbarSettings();
  }, []);

  useEffect(() => {
    const handleSettingsUpdate = () => {
      loadNavbarSettings();
      loadCategories();
    };
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const handleWhatsAppClick = () => {
    const phoneNumber = '923001234567';
    const message = 'Hi! I would like to know more about your products.';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    const handleOpenSideCart = () => setIsCartOpen(true);
    window.addEventListener('openSideCart', handleOpenSideCart);
    return () => window.removeEventListener('openSideCart', handleOpenSideCart);
  }, []);

  return (
    <>
      {navbarSettings.announcement_enabled === '1' && navbarSettings.announcement_text && (
        <div className="topbar-nav">
          {navbarSettings.announcement_text}
        </div>
      )}

      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          {/* Toggle — only visible at tablet/mobile widths via CSS.
              Icon flips to X when open, itself doubling as the close
              control — no separate overlay or close button needed. */}
          <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link to="/" className="nav-logo" onClick={() => setIsMenuOpen(false)}>
            {settings.logo && !logoError ? (
              <img
                src={`${settings.logo}?v=${Date.now()}`}
                alt={settings.store_name}
                className="logo-image"
                style={{
                  height: navbarSettings.logo_height ? `${navbarSettings.logo_height}px` : '45px',
                  width: navbarSettings.logo_width ? `${navbarSettings.logo_width}px` : 'auto',
                }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="nav-logo-text"><span className="logo-dot">.</span></span>
            )}
          </Link>

          {/* Desktop: horizontal row, inline between logo and actions.
              Tablet/mobile: collapses to 0 height, expands as a full-width
              panel below the top row when isMenuOpen — pushing page
              content down, since .navbar is sticky, not fixed. */}
          <ul className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <li>
              <Link
                to="/shop?sort=new-arrivals"
                className="nav-link"
                onClick={handleNewArrivalsClick}
              >
                New Arrivals
              </Link>
            </li>

            {!isLoading && categories.map((category) => (
              <li key={category}>
                <Link
                  to={`/shop?category=${category}`}
                  className={`nav-link ${new URLSearchParams(location.search).get('category') === category ? 'active' : ''}`}
                  onClick={(e) => handleCategoryClick(category, e)}
                >
                  {getCategoryDisplayName(category)}
                </Link>
              </li>
            ))}
            {isLoading && (
              <li><span className="nav-link-loading" style={{ display: 'none' }}></span></li>
            )}

            <li>
              <Link
                to="/shop?sort=sale"
                className="nav-link"
                onClick={handleSaleClick}
              >
                Sale
              </Link>
            </li>
          </ul>

          <div className="nav-actions">
            <Link to="/search" className="navs-icon-btn search-btn">
              <Search size={19} />
            </Link>

            <button className="nav-icon-btn cart-btn" onClick={() => setIsCartOpen(true)}>
              <ShoppingBag size={20} />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
            <SideCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

            {/* Hidden entirely below 600px (mobile) via CSS — visible on
                both desktop and tablet. */}
            <button className="nav-icon-btn whatsapp-nav-btn" onClick={handleWhatsAppClick}>
              <MessageCircle size={16} />
              <span className="whatsapp-label">WhatsApp</span>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;